import React, { useCallback, useMemo } from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import ScrollableTooltipContent from '@sharedCR/common/ScrollableTooltipContent';
import { WarningIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT_FOR_TREE_ITEM } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { FeeCureType } from 'constants/feeTypes';
import { currencyOptions } from 'constants/loanFee';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { getPendingChangesActionType } from 'utils/loanFees';
import { formatNumberToCurrencyString } from 'utils/number';

import { footerStyles, pendingChangeCellStyles, titleTypographyStyles } from '../commonStyled';

import SummaryTableCell from './components/SummaryTableCell';
import { TEN_PERCENT_TOLERANCE_COEFFICIENT } from './CureData.constants';
import {
  cureHeaderStyles,
  cureTableContainerStyles,
  footerCellStyles,
  getTableBodyStyles,
  getTableFooterStyles,
  getTableHeadStyles,
  texasTableBodyStyles,
  totalRowsStyles,
} from './CureData.styles';
import {
  getCureItemsByCureType,
  getCureTableColumnsCount,
  hasItemsWithPendingChanges,
} from './CureData.utils';

const defaultObject = {};

const CureData = ({ data = defaultObject }) => {
  const {
    cureItems = [],
    totalCure,
    baselineDate,
    leDate,
    texasCashOutCure,
    isCureHeld,
    modifiedFields: cureSummaryModifiedFields,
  } = data;
  const { maxAllowableFees, sectionDE, fees = [], total } = texasCashOutCure || {};

  const isInitialLeColumnShown = useFeatureToggle(FeatureFlag.ShowInitialLeCurePopupColumn);
  const isMergeCureBaselinesEnabled = useFeatureToggle(FeatureFlag.EnableMergeCureBaselines);

  const hasPendingChanges = useMemo(
    () => !!cureSummaryModifiedFields?.length || hasItemsWithPendingChanges(cureItems),
    [cureItems, cureSummaryModifiedFields],
  );

  const { zeroCureItems, tenPercentCureItems, transferTaxCureItems } = useMemo(
    () => getCureItemsByCureType(cureItems),
    [cureItems],
  );

  const { currentAmountTotal, baselineAmountTotal, maxAllowableTotal } = useMemo(() => {
    const baselineAmountTotal = _.sumBy(tenPercentCureItems, 'baselineAmount');

    return {
      currentAmountTotal: _.sumBy(tenPercentCureItems, 'amount'),
      baselineAmountTotal,
      maxAllowableTotal: baselineAmountTotal * TEN_PERCENT_TOLERANCE_COEFFICIENT,
    };
  }, [tenPercentCureItems]);

  const renderTableHead = useCallback(
    () => (
      <TableHead
        className="table-head"
        sx={getTableHeadStyles({ isInitialLeColumnShown, hasPendingChanges })}
      >
        <TableRow className="table-row">
          <TableCell align="left" className="table-cell">
            Fee Name
          </TableCell>
          {hasPendingChanges && (
            <TableCell align="right" className="table-cell">
              Pending Change
            </TableCell>
          )}
          <TableCell align="right" className="table-cell">
            {hasPendingChanges ? 'Last Save' : 'Current Fee'}
          </TableCell>
          {isInitialLeColumnShown && (
            <TableCell align="right" className="table-cell">
              <Stack>
                <span>Initial LE</span>
                <span>
                  {leDate ? dayjs(leDate).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM) : 'N/A'}
                </span>
              </Stack>
            </TableCell>
          )}
          <TableCell align="right" className="table-cell">
            <Stack>
              <span>Baseline</span>
              {!isMergeCureBaselinesEnabled && (
                <span>
                  {baselineDate
                    ? dayjs(baselineDate).format(COMMON_DATETIME_FORMAT_FOR_TREE_ITEM)
                    : 'N/A'}
                </span>
              )}
            </Stack>
          </TableCell>
          <TableCell align="right" className="table-cell">
            Cure
          </TableCell>
        </TableRow>
      </TableHead>
    ),
    [baselineDate, leDate, isInitialLeColumnShown, isMergeCureBaselinesEnabled, hasPendingChanges],
  );

  const renderTableBodyRows = useCallback(
    ({ items }) =>
      items?.length ? (
        items.map((item, ind) => {
          const {
            name,
            amount,
            savedAmount,
            leAmount,
            baselineAmount,
            cure,
            cureType,
            modifiedFields: itemModifiedFields,
          } = item;

          return (
            <TableRow className="table-row" key={`${name}-${ind}`}>
              <TableCell align="left" className="table-cell">
                {name}
              </TableCell>
              {hasPendingChanges && (
                <TableCell align="right" className="table-cell">
                  <PendingChangeWrapper
                    isSmall
                    pendingChangesActionType={getPendingChangesActionType(
                      itemModifiedFields,
                      'amount',
                    )}
                    sx={pendingChangeCellStyles}
                  >
                    {formatNumberToCurrencyString(amount ?? 0, currencyOptions)}
                  </PendingChangeWrapper>
                </TableCell>
              )}
              <TableCell align="right" className="table-cell">
                {formatNumberToCurrencyString(
                  hasPendingChanges ? (savedAmount ?? amount ?? 0) : (amount ?? 0),
                  currencyOptions,
                )}
              </TableCell>
              {isInitialLeColumnShown && (
                <TableCell align="right" className="table-cell">
                  <PendingChangeWrapper
                    isSmall
                    pendingChangesActionType={getPendingChangesActionType(
                      itemModifiedFields,
                      'leAmount',
                    )}
                    sx={pendingChangeCellStyles}
                  >
                    {formatNumberToCurrencyString(leAmount ?? 0, currencyOptions)}
                  </PendingChangeWrapper>
                </TableCell>
              )}
              <TableCell align="right" className="table-cell">
                <PendingChangeWrapper
                  isSmall
                  pendingChangesActionType={getPendingChangesActionType(
                    itemModifiedFields,
                    'baselineAmount',
                  )}
                  sx={pendingChangeCellStyles}
                >
                  {formatNumberToCurrencyString(baselineAmount ?? 0, currencyOptions)}
                </PendingChangeWrapper>
              </TableCell>
              <TableCell align="right" className="table-cell">
                <PendingChangeWrapper
                  isSmall
                  pendingChangesActionType={
                    cureType !== FeeCureType.TEN_PERCENT_TOLERANCE
                      ? getPendingChangesActionType(itemModifiedFields, 'cure')
                      : null
                  }
                  sx={pendingChangeCellStyles}
                >
                  {formatNumberToCurrencyString(
                    !_.isNil(cure) && cureType !== FeeCureType.TEN_PERCENT_TOLERANCE ? cure : 0,
                    currencyOptions,
                  )}
                </PendingChangeWrapper>
              </TableCell>
            </TableRow>
          );
        })
      ) : (
        <TableRow className="table-row">
          <TableCell
            className="table-cell"
            colSpan={getCureTableColumnsCount({ isInitialLeColumnShown, hasPendingChanges })}
          />
        </TableRow>
      ),
    [isInitialLeColumnShown, hasPendingChanges],
  );

  const renderTableFooter = useCallback(
    ({ items, text, isSumCalculated = true }) => {
      const hasItemChanges = hasItemsWithPendingChanges(items);

      return (
        <TableFooter
          className="table-footer"
          sx={getTableFooterStyles({ isInitialLeColumnShown, hasPendingChanges })}
        >
          <TableRow className="table-row">
            <TableCell align="left" className="table-cell">
              {text}
            </TableCell>
            {hasPendingChanges && <TableCell align="right" className="table-cell" />}
            <TableCell align="right" className="table-cell" />
            {isInitialLeColumnShown && <TableCell align="right" className="table-cell" />}
            <TableCell align="right" className="table-cell" />
            <TableCell align="right" className="table-cell">
              <PendingChangeWrapper
                isSmall
                pendingChangesActionType={hasItemChanges ? PendingChangesActionType.MODIFIED : null}
                sx={pendingChangeCellStyles}
              >
                {formatNumberToCurrencyString(
                  isSumCalculated ? _.sumBy(items, 'cure') : 0,
                  currencyOptions,
                )}
              </PendingChangeWrapper>
            </TableCell>
          </TableRow>
        </TableFooter>
      );
    },
    [isInitialLeColumnShown, hasPendingChanges],
  );

  return (
    <ScrollableTooltipContent
      footer={
        <Box className="w-full flex justify-between" sx={footerStyles}>
          <Box sx={footerCellStyles} textAlign="left">
            Total Cure:
          </Box>
          <Box sx={footerCellStyles} textAlign="right">
            <PendingChangeWrapper
              isSmall
              pendingChangesActionType={getPendingChangesActionType(
                cureSummaryModifiedFields,
                'totalCure',
              )}
              sx={pendingChangeCellStyles}
            >
              {formatNumberToCurrencyString(totalCure ?? 0, currencyOptions)}
            </PendingChangeWrapper>
          </Box>
        </Box>
      }
    >
      <Box sx={cureHeaderStyles}>
        <Typography sx={titleTypographyStyles} variant="regular14">
          Cure Amount
        </Typography>
        {isCureHeld && (
          <LabelCDS
            size={LabelSize.S}
            title={
              <>
                <WarningIcon size={IconSize[16]} />
                Cures have not yet been issued while the cure credit is held
              </>
            }
            variant={LabelVariant.ORANGE}
          />
        )}
      </Box>
      {/* ZeroTolerance cure type fees */}
      <TableContainer className="table-container" sx={cureTableContainerStyles}>
        <Table className="table">
          {renderTableHead()}

          <TableBody
            className="table-body"
            sx={getTableBodyStyles({ isInitialLeColumnShown, hasPendingChanges })}
          >
            {renderTableBodyRows({ items: tenPercentCureItems })}

            <TableRow className="table-row" sx={totalRowsStyles}>
              <SummaryTableCell>Aggregate 10% Total</SummaryTableCell>
              {hasPendingChanges && (
                <SummaryTableCell align="right">
                  <PendingChangeWrapper
                    isSmall
                    pendingChangesActionType={
                      hasItemsWithPendingChanges(tenPercentCureItems)
                        ? PendingChangesActionType.MODIFIED
                        : null
                    }
                    sx={pendingChangeCellStyles}
                  >
                    {formatNumberToCurrencyString(currentAmountTotal, currencyOptions)}
                  </PendingChangeWrapper>
                </SummaryTableCell>
              )}
              <SummaryTableCell align="right">
                {hasPendingChanges
                  ? null
                  : formatNumberToCurrencyString(currentAmountTotal, currencyOptions)}
              </SummaryTableCell>
              {isInitialLeColumnShown && (
                <SummaryTableCell align="right">
                  {formatNumberToCurrencyString(0, currencyOptions)}
                </SummaryTableCell>
              )}
              <SummaryTableCell align="right">
                {formatNumberToCurrencyString(baselineAmountTotal, currencyOptions)}
              </SummaryTableCell>
              <SummaryTableCell align="right">
                {formatNumberToCurrencyString(0, currencyOptions)}
              </SummaryTableCell>
            </TableRow>
            <TableRow className="table-row" sx={totalRowsStyles}>
              <SummaryTableCell>Max allowable 10% (110% of Baseline 10%)</SummaryTableCell>
              {hasPendingChanges && (
                <SummaryTableCell align="right">
                  {formatNumberToCurrencyString(0, currencyOptions)}
                </SummaryTableCell>
              )}
              <SummaryTableCell align="right" />
              {isInitialLeColumnShown && (
                <SummaryTableCell align="right">
                  {formatNumberToCurrencyString(0, currencyOptions)}
                </SummaryTableCell>
              )}
              <SummaryTableCell align="right">
                {formatNumberToCurrencyString(maxAllowableTotal, currencyOptions)}
              </SummaryTableCell>
              <SummaryTableCell align="right">
                {formatNumberToCurrencyString(0, currencyOptions)}
              </SummaryTableCell>
            </TableRow>
          </TableBody>

          {renderTableFooter({
            items: tenPercentCureItems,
            text: 'Total Cure for 10%',
            isSumCalculated: currentAmountTotal - maxAllowableTotal > 0,
          })}
        </Table>
      </TableContainer>
      {/* TenPercentTolerance cure type fees */}
      <TableContainer className="table-container" sx={cureTableContainerStyles}>
        <Table className="table">
          {renderTableHead()}

          <TableBody
            className="table-body"
            sx={getTableBodyStyles({ isInitialLeColumnShown, hasPendingChanges })}
          >
            {renderTableBodyRows({ items: zeroCureItems })}
          </TableBody>

          {renderTableFooter({ items: zeroCureItems, text: 'Total Cure for 0%' })}
        </Table>
      </TableContainer>

      {/* Transfer Taxes */}
      <TableContainer className="table-container" sx={cureTableContainerStyles}>
        <Table className="table">
          {renderTableHead()}

          <TableBody
            className="table-body"
            sx={getTableBodyStyles({ isInitialLeColumnShown, hasPendingChanges })}
          >
            {renderTableBodyRows({ items: transferTaxCureItems })}
          </TableBody>

          {renderTableFooter({ items: transferTaxCureItems, text: 'Transfer Tax Total' })}
        </Table>
      </TableContainer>

      {!_.isNil(texasCashOutCure) && (
        <>
          <Typography sx={titleTypographyStyles} variant="regular14">
            Texas Cash-out Cure
          </Typography>

          <TableContainer className="table-container" sx={cureTableContainerStyles}>
            <Table className="table">
              <TableBody className="table-body" sx={texasTableBodyStyles}>
                <TableRow className="table-row">
                  <TableCell align="left" className="table-cell">
                    Maximum Allowable Fees:{' '}
                    {formatNumberToCurrencyString(maxAllowableFees ?? 0, currencyOptions)}
                  </TableCell>
                  <TableCell align="left" className="table-cell">
                    Section D + E:
                  </TableCell>
                  <TableCell align="right" className="table-cell">
                    {formatNumberToCurrencyString(sectionDE ?? 0, currencyOptions)}
                  </TableCell>
                </TableRow>

                {!!fees?.length &&
                  fees.map(({ name, value, modifiedFields: feeModifiedFields }, ind) => (
                    <TableRow className="table-row" key={`${name}-${ind}`}>
                      <TableCell className="table-cell" />
                      <TableCell align="left" className="table-cell">
                        {name}:
                      </TableCell>
                      <TableCell align="right" className="table-cell">
                        <PendingChangeWrapper
                          isSmall
                          pendingChangesActionType={getPendingChangesActionType(
                            feeModifiedFields,
                            'value',
                          )}
                          sx={pendingChangeCellStyles}
                        >
                          {formatNumberToCurrencyString(value ?? 0, currencyOptions)}
                        </PendingChangeWrapper>
                      </TableCell>
                    </TableRow>
                  ))}

                <TableRow className="table-row">
                  <TableCell className="table-cell" />
                  <TableCell align="left" className="table-cell">
                    Group Total:
                  </TableCell>
                  <TableCell align="right" className="table-cell">
                    {formatNumberToCurrencyString(total ?? 0, currencyOptions)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </ScrollableTooltipContent>
  );
};

CureData.propTypes = {
  data: PropTypes.shape({
    cureItems: PropTypes.arrayOf(
      PropTypes.shape({
        feeTypeId: PropTypes.string,
        name: PropTypes.string,
        cureType: PropTypes.string,
        amount: PropTypes.number,
        savedAmount: PropTypes.number,
        leAmount: PropTypes.number,
        baselineAmount: PropTypes.number,
        cure: PropTypes.number,
      }),
    ),
    totalCure: PropTypes.number,
    isCureHeld: PropTypes.bool,
    modifiedFields: PropTypes.arrayOf(PropTypes.string),
    leDate: PropTypes.string,
    baselineDate: PropTypes.string,
    texasCashOutCure: PropTypes.shape({
      maxAllowableFees: PropTypes.number,
      sectionDE: PropTypes.number,
      fees: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.number,
        }),
      ),
      total: PropTypes.number,
    }),
  }),
};

export default CureData;
